import React, { useState } from "react";
import axios from "axios";
import Config from "../Config";
import { FacebookIcon, InstagramIcon } from "../icons/SocialIcons";


interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    subject?: string;
    message: string;
    dropdown: string;
}

interface ContactFormProps {
    showPhone?: boolean;
    showSubject?: boolean;
    subjectText?: string;
    headingText?: string;
    subText?: string;
    headingClassName?: string; // <-- Add this line for dynamic heading class
    twoColumn?: boolean;
    socialIcons?: boolean;
    submitButtonText?: string;
    preferredInput?: 'textarea' | 'text';
    messageName?: string;
    showDropdown?: boolean;
    dropdownLabel?: string;
    dropdownOptions?: string[];
    defaultDropdownValue?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({
    showPhone = true,
    showSubject = true,
    subjectText = "Subject",
    headingText = "Contact Us",
    subText = "",
    headingClassName = "", // <-- Add this line for dynamic heading class
    twoColumn = false, // Default to 1 column
    socialIcons = false, // Add this prop for social icons
    submitButtonText = "Send", // Add default value
    preferredInput = 'textarea',
    messageName = 'Message',
    showDropdown = false,
    dropdownLabel = "Select Option",
    dropdownOptions = [],
    defaultDropdownValue = "",
}) => {
    const [formData, setFormData] = useState<FormData>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        dropdown: defaultDropdownValue || dropdownOptions[0], // Initialize with default value
    });

    const [submitted, setSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [phoneError, setPhoneError] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;

        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (name === "phone") {
            if (!validatePhone(value)) {
                setPhoneError("Invalid phone number");
            } else {
                setPhoneError("");
            }
        }

        if (name === "email") {
            if (!validateEmail(value)) {
                setEmailError("Invalid email address");
            } else {
                setEmailError("");
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const { firstName, lastName, email, message } = formData;
        if (!validateEmail(formData.email)) {
            alert("Please enter a valid email address.");
            return;
        }

        if (!validatePhone(formData.phone ?? "")) {
            alert("Please enter a valid phone number.");
            return;
        }

        if (!firstName || !lastName || !email || !message) {
            alert("Please fill out all required fields.");
            return;
        }

        // Create submission data with combined name
        const submissionData = {
            name: `${firstName} ${lastName}`,
            email,
            phone: formData.phone,
            subject: subjectText === 'Subject' ? 'Contact Us' : subjectText,
            message: messageName === 'Refered By'
                ? `Referred by: ${formData.message}, Drop Off Location: ${formData.dropdown}`
                : formData.message,
        };

        try {
            var response = await axios.post(
                `${Config.apiUrl}/contactus/${Config.tenant}/tenant`,
                submissionData,            // <-- pass formData directly as body (JS object)
                {
                    headers: {
                        'Content-Type': 'application/json'  // <-- headers go in 3rd argument
                    }
                }
            );

            if (response.status === 200) {
                setSubmitted(true);
                setErrorMessage(""); // Clear any previous error message
                setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    subject: "",
                    message: "",
                    dropdown: "",
                });
            }
        } catch (err) {
            console.error(err);
            setSubmitted(false);
            setErrorMessage("Something went wrong. Please try again.");
        }
    };

    const validatePhone = (phone: string) => {
        if (!phone) return true; // optional field, so empty is valid
        // Basic US phone regex: allows (123) 456-7890, 123-456-7890, 1234567890, +1 123 456 7890 etc
        const phoneRegex = /^(\+?\d{1,2}[\s.-]?)?(\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}$/;
        return phoneRegex.test(phone);
    };

    const validateEmail = (email: string) => {
        if (!email) return false; // email is required in your form
        // Basic email regex pattern
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    return (
        <section className="bg-[#fafaf3] py-8 px-4">
            <div
                className={`w-[80vw] md:w-[60vw] mx-auto ${twoColumn ? "max-w-3xl" : "max-w-lg"
                    }`}
            >                <h2 className={`font-mono text-3xl sm:text-3xl md:text-4xl mb-2 text-center ${headingClassName}`}>
                    {headingText}
                </h2>
                <p className="text-gray-700 mb-2 font-sans">
                    {subText}
                </p>

                {socialIcons && (
                    <div className="flex items-start gap-4 mb-4">
                        <InstagramIcon
                            url="https://www.instagram.com/1earthcomposting/"
                            label="Instagram"
                            size={30}
                            color="#E1306C"
                            className="hover:text-pink-600 transition-colors ml-4"
                        />

                        <FacebookIcon
                            url="https://www.facebook.com/1EarthComposting"
                            label="Facebook"
                            size={30}
                            color="#1877F2"
                            className="hover:text-blue-600 transition-colors"
                        />
                    </div>
                )}
                <form
                    onSubmit={handleSubmit}
                    className={
                        twoColumn
                            ? "grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-x-8 gap-y-4"
                            : "flex flex-col gap-4"
                    }
                >
                    {/* Left column */}
                    <div className="flex flex-col gap-6">
                        {/* Name fields - conditional grid based on twoColumn */}
                        <div className={!twoColumn ? "grid grid-cols-2 gap-4" : "space-y-4"}>
                            <div>
                                <label htmlFor="firstName" className="block text-sm mb-2 font-sans text-[#232b18]">
                                    First Name<span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="firstName"
                                    name="firstName"
                                    type="text"
                                    placeholder="First name"
                                    required
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className="w-full h-10 border border-[#232b18] px-3 py-2 font-sans"
                                />
                            </div>
                            <div>
                                <label htmlFor="lastName" className="block text-sm mb-2 font-sans text-[#232b18]">
                                    Last Name<span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="lastName"
                                    name="lastName"
                                    type="text"
                                    placeholder="Last name"
                                    required
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="w-full h-10 border border-[#232b18] px-3 py-2 font-sans"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm mb-2 font-sans text-[#232b18]">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="Enter your email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className={`w-full h-10 border px-3 py-2 font-sans ${emailError ? "border-red-600" : "border-[#232b18]"
                                    }`}
                            />
                            {emailError && <p className="text-red-600 text-sm mt-1">{emailError}</p>}
                        </div>

                        {/* Optional fields with consistent spacing */}
                        {showPhone && (
                            <div>
                                <label htmlFor="phone" className="block text-sm mb-2 font-sans text-[#232b18]">
                                    Phone
                                </label>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    placeholder="Optional"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className={`w-full h-10 border px-3 py-2 font-sans ${phoneError ? "border-red-600" : "border-[#232b18]"
                                        }`}
                                />
                                {phoneError && <p className="text-red-600 text-sm mt-1">{phoneError}</p>}
                            </div>
                        )}

                        {showSubject && (
                            <div>
                                <label htmlFor="subject" className="block text-sm mb-2 font-sans text-[#232b18]">
                                    {subjectText}
                                </label>
                                <input
                                    id="subject"
                                    name="subject"
                                    type="text"
                                    placeholder="Enter subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    className="w-full h-10 border border-[#232b18] px-3 py-2 font-sans"
                                />
                            </div>
                        )}
                    </div>

                    {/* Right column */}
                    <div className="flex flex-col flex-1 min-w-0">
                        <div className="flex-1 mb-4">
                            <label htmlFor={messageName} className="block text-sm mb-2 font-sans text-[#232b18]">
                                {messageName}
                            </label>
                            {preferredInput === 'textarea' ? (
                                <textarea
                                    id={messageName}
                                    name="message"  // Changed from messageName to "message"
                                    placeholder="Type your message here..."
                                    rows={8}
                                    required
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="w-full border border-[#232b18] px-3 py-2 font-sans resize-none"
                                />
                            ) : (
                                <input
                                    id={messageName}
                                    name="message"  // Changed from messageName to "message"
                                    type="text"
                                    placeholder={`Enter your ${messageName.toLowerCase()}`}
                                    required
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="w-full h-10 border border-[#232b18] px-3 py-2 font-sans"
                                />
                            )}
                        </div>

                        {/* Add this before the message field */}
                        {showDropdown && (
                            <div className="mb-4">
                                <label htmlFor="dropdown" className="block text-sm mb-2 font-sans text-[#232b18]">
                                    {dropdownLabel}
                                </label>
                               <select
  id="dropdown"
  name="dropdown"
  value={formData.dropdown}
  onChange={handleChange}
  className="w-full h-10 border border-[#232b18] px-3 py-2 font-sans bg-white"
  required
>
  {!formData.dropdown && (
    <option value="" disabled>
      {defaultDropdownValue || "Select..."}
    </option>
  )}
  {dropdownOptions.map((option, index) => (
    <option key={index} value={option}>
      {option}
    </option>
  ))}
</select>
                            </div>
                        )}

                        <div className="flex justify-end mt-4">
                            <button
                                type="submit"
                                className="bg-[#b05e2c] text-white px-8 py-2 font-sans hover:bg-[#924d24]"
                            >
                                {submitButtonText}
                            </button>
                        </div>
                    </div>

                </form>
                {/* Success/Error messages */}
                {submitted && (
                    <p className="text-green-600 font-medium text-center mt-4">
                        Message sent successfully!
                    </p>
                )}
                {errorMessage && (
                    <p className="text-red-600 font-medium text-center mt-4">
                        {errorMessage}
                    </p>
                )}
            </div>
        </section>
    );
};

export default ContactForm;